import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import './Reports.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Format decimal hours as "Xh Ym"
const formatHours = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0 && m === 0) return '0h';
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

function Reports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [department, setDepartment] = useState('all');
  const [role, setRole] = useState('all');
  const [trainingCategory, setTrainingCategory] = useState('all'); // Add category filter
  const [departments, setDepartments] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set default dates (last 30 days)
  useEffect(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 30);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  }, []);

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/employees`);
        const uniqueDepts = [...new Set(response.data.map(emp => emp.department).filter(Boolean))];
        setDepartments(uniqueDepts.sort());
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  const fetchReport = async () => {
    if (!startDate || !endDate) {
      setError('Please select start and end dates');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        department: department,
        role: role,
        training_category: trainingCategory // Add category to params
      });

      const response = await axios.get(`${API_BASE_URL}/training/report?${params}`);
      setReportData(response.data);
    } catch (err) {
      setError('Failed to load data: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    if (!reportData || !reportData.records.length) {
      alert('No data to export');
      return;
    }

    // Prepare data for Excel
    const excelData = reportData.records.map((record, index) => ({
      'No.': index + 1,
      'Employee ID': record.employee_id,
      'Employee Name': record.employee_name,
      'Department': record.department || '-',
      'Position': record.position || '-',
      'Level': record.level || '-',
      'Role': record.role === 'trainer' ? 'Trainer' : 'Trainee',
      'Date': record.training_date,
      'Training Topic': record.training_topic,
      'Hours': record.training_hours,
      'Trainer': record.trainer_name || '-',
      'Notes': record.notes || '-'
    }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 8 },  // No.
      { wch: 12 }, // Employee ID
      { wch: 25 }, // Employee Name
      { wch: 20 }, // Department
      { wch: 25 }, // Position
      { wch: 10 }, // Level
      { wch: 12 }, // Role
      { wch: 12 }, // Date
      { wch: 35 }, // Training Topic
      { wch: 12 }, // Hours
      { wch: 25 }, // Trainer
      { wch: 30 }  // Notes
    ];

    XLSX.utils.book_append_sheet(wb, ws, 'Training Report');

    // Add summary sheet
    const summaryData = [
      { 'Item': 'Date Range', 'Value': `${startDate} to ${endDate}` },
      { 'Item': 'Department', 'Value': department === 'all' ? 'All' : department },
      { 'Item': 'Role', 'Value': role === 'all' ? 'All' : (role === 'trainer' ? 'Trainer' : 'Trainee') },
      { 'Item': '', 'Value': '' },
      { 'Item': 'Total Records', 'Value': reportData.summary.total_records },
      { 'Item': 'Total Hours', 'Value': reportData.summary.total_hours.toFixed(2) },
      { 'Item': 'Total Employees', 'Value': reportData.summary.unique_employees }
    ];

    const wsSummary = XLSX.utils.json_to_sheet(summaryData);
    wsSummary['!cols'] = [{ wch: 25 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');

    // Generate filename
    const filename = `Training_Report_${startDate}_to_${endDate}.xlsx`;

    // Download
    XLSX.writeFile(wb, filename);
  };

  const setQuickDate = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  };

  const setThisYear = () => {
    const now = new Date();
    setStartDate(`${now.getFullYear()}-01-01`);
    setEndDate(`${now.getFullYear()}-12-31`);
  };

  const setAllTime = () => {
    setStartDate('2020-01-01');
    setEndDate('2030-12-31');
  };

  const exportToPDF = () => {
    if (!reportData || !reportData.records.length) {
      alert('No data to export');
      return;
    }

    const s = reportData.summary;
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const M = 14; // page margin

    // Brand palette (RGB)
    const terracotta = [196, 97, 47];
    const terracottaLt = [212, 115, 94];
    const teal = [74, 122, 140];
    const ink = [26, 26, 26];
    const muted = [107, 107, 107];
    const track = [239, 233, 221];
    const cardBg = [253, 251, 247];
    const cardBorder = [232, 227, 214];

    // ─── Header banner ───
    doc.setFillColor(...terracotta);
    doc.rect(0, 0, pageW, 34, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(20);
    doc.text('Training Dashboard', M, 16);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    doc.setTextColor(255, 240, 232);
    doc.text(`${startDate}  to  ${endDate}`, M, 24);
    const deptLabel = department === 'all' ? 'All Departments' : department;
    const roleLabel = role === 'all' ? 'All Roles' : (role === 'trainer' ? 'Trainer' : 'Trainee');
    doc.text(`${deptLabel}  •  ${roleLabel}`, M, 30);
    const genDate = new Date().toLocaleDateString('en-GB', { timeZone: 'Asia/Bangkok' });
    doc.text(`Generated: ${genDate}`, pageW - M, 30, { align: 'right' });

    const avgPerEmployee = s.unique_employees > 0 ? s.total_hours / s.unique_employees : 0;

    // ─── KPI cards ───
    let y = 44;
    const kpis = [
      { label: 'TOTAL SESSIONS', value: String(s.total_records) },
      { label: 'TOTAL HOURS', value: formatHours(s.total_hours), accent: true },
      { label: 'EMPLOYEES', value: String(s.unique_employees) },
      { label: 'AVG / EMPLOYEE', value: formatHours(avgPerEmployee) },
    ];
    const gap = 6;
    const cardW = (pageW - M * 2 - gap * 3) / 4;
    const cardH = 26;
    kpis.forEach((k, i) => {
      const x = M + i * (cardW + gap);
      if (k.accent) {
        doc.setFillColor(...terracottaLt);
        doc.roundedRect(x, y, cardW, cardH, 2.5, 2.5, 'F');
        doc.setTextColor(255, 240, 232);
      } else {
        doc.setFillColor(...cardBg);
        doc.setDrawColor(...cardBorder);
        doc.roundedRect(x, y, cardW, cardH, 2.5, 2.5, 'FD');
        doc.setTextColor(...muted);
      }
      doc.setFont(undefined, 'normal');
      doc.setFontSize(7);
      doc.text(k.label, x + 5, y + 8);
      if (k.accent) doc.setTextColor(255, 255, 255);
      else doc.setTextColor(...ink);
      doc.setFont(undefined, 'bold');
      doc.setFontSize(16);
      doc.text(k.value, x + 5, y + 19);
    });

    // ─── Department bar chart ───
    y += cardH + 12;
    doc.setTextColor(...ink);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Training Hours by Department', M, y);
    y += 6;

    const deptRows = Object.entries(s.by_department || {})
      .filter(([name]) => name && name !== 'null')
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => b.hours - a.hours);
    const maxHours = deptRows.length ? Math.max(...deptRows.map(d => d.hours)) : 0;

    const labelW = 42;
    const barX = M + labelW;
    const barMaxW = pageW - M - barX - 26;
    // Shrink row height when many departments so all fit on the page
    const rowH = deptRows.length > 11 ? 6.5 : 8;

    deptRows.forEach((d) => {
      // label
      doc.setFont(undefined, 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...ink);
      const name = d.name.length > 22 ? d.name.substring(0, 21) + '…' : d.name;
      doc.text(name, barX - 3, y + 4, { align: 'right' });
      // track
      doc.setFillColor(...track);
      doc.roundedRect(barX, y, barMaxW, 5.5, 1.2, 1.2, 'F');
      // fill
      const w = maxHours > 0 ? (d.hours / maxHours) * barMaxW : 0;
      doc.setFillColor(...terracotta);
      if (w > 0) doc.roundedRect(barX, y, Math.max(w, 2), 5.5, 1.2, 1.2, 'F');
      // value
      doc.setTextColor(...muted);
      doc.setFontSize(7.5);
      doc.text(formatHours(d.hours), barX + barMaxW + 3, y + 4);
      y += rowH;
    });

    // ─── Role split bar ───
    y += 6;
    doc.setTextColor(...ink);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(12);
    doc.text('Trainer vs Trainee Hours', M, y);
    y += 6;

    const trainerHours = s.by_role?.trainer?.hours || 0;
    const traineeHours = s.by_role?.trainee?.hours || 0;
    const trainerCount = s.by_role?.trainer?.count || 0;
    const traineeCount = s.by_role?.trainee?.count || 0;
    const roleTotal = trainerHours + traineeHours || 1;
    const splitW = pageW - M * 2;
    const trainerW = (trainerHours / roleTotal) * splitW;

    doc.setFillColor(...track);
    doc.roundedRect(M, y, splitW, 8, 1.5, 1.5, 'F');
    doc.setFillColor(...terracotta);
    if (trainerW > 0) doc.roundedRect(M, y, Math.max(trainerW, 2), 8, 1.5, 1.5, 'F');
    doc.setFillColor(...teal);
    const traineeW = splitW - trainerW;
    if (traineeW > 0) doc.roundedRect(M + trainerW, y, traineeW, 8, 1.5, 1.5, 'F');
    y += 14;

    // legend
    doc.setFillColor(...terracotta);
    doc.roundedRect(M, y - 3, 4, 4, 0.8, 0.8, 'F');
    doc.setTextColor(...ink);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(9);
    doc.text('Trainer', M + 6, y);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...muted);
    doc.setFontSize(8);
    doc.text(`${formatHours(trainerHours)} · ${trainerCount} sessions`, M + 24, y);

    doc.setFillColor(...teal);
    doc.roundedRect(pageW / 2, y - 3, 4, 4, 0.8, 0.8, 'F');
    doc.setTextColor(...ink);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(9);
    doc.text('Trainee', pageW / 2 + 6, y);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(...muted);
    doc.setFontSize(8);
    doc.text(`${formatHours(traineeHours)} · ${traineeCount} sessions`, pageW / 2 + 24, y);

    // footer on page 1
    doc.setTextColor(...muted);
    doc.setFontSize(7);
    doc.text('MH Training Tracker', M, pageH - 8);

    // ─── Page 2: detailed records ───
    doc.addPage();
    doc.setTextColor(...ink);
    doc.setFont(undefined, 'bold');
    doc.setFontSize(14);
    doc.text('Detailed Records', M, 18);

    const recordRows = reportData.records.map((r, i) => [
      i + 1,
      r.employee_name,
      r.department || '-',
      r.role === 'trainer' ? 'Trainer' : 'Trainee',
      r.training_date,
      r.training_topic,
      r.training_hours,
    ]);

    autoTable(doc, {
      startY: 24,
      head: [['No.', 'Name', 'Department', 'Role', 'Date', 'Topic', 'Hours']],
      body: recordRows,
      theme: 'striped',
      headStyles: { fillColor: terracotta, textColor: 255 },
      styles: { fontSize: 8, cellPadding: 2 },
      alternateRowStyles: { fillColor: [250, 247, 241] },
      columnStyles: { 5: { cellWidth: 50 } },
      margin: { left: M, right: M },
    });

    doc.save(`Training_Dashboard_${startDate}_to_${endDate}.pdf`);
  };

  return (
    <div className="reports-container">
      <div className="reports-header">
        <div>
          <h1 className="page-title">
            Training <em>Reports</em>
          </h1>
          <p className="page-subtitle">Training data reports</p>
        </div>
      </div>

      <div className="report-filters">
        <div className="filter-section">
          <h3>Select Period</h3>

          <div className="quick-dates">
            <button onClick={() => setQuickDate(7)} className="quick-date-btn">Last 7 Days</button>
            <button onClick={() => setQuickDate(30)} className="quick-date-btn">Last 30 Days</button>
            <button onClick={() => setQuickDate(90)} className="quick-date-btn">Last 90 Days</button>
            <button onClick={setThisYear} className="quick-date-btn">This Year</button>
            <button onClick={setAllTime} className="quick-date-btn">All Time</button>
          </div>

          <div className="date-inputs">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="form-input"
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="form-input"
              />
            </div>
          </div>
        </div>

        <div className="filter-section">
          <h3>Filter Data</h3>
          <div className="filter-inputs">
            <div className="form-group">
              <label>Department</label>
              <select
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                className="form-input"
              >
                <option value="all">All</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="form-input"
              >
                <option value="all">All</option>
                <option value="trainee">Trainee</option>
                <option value="trainer">Trainer</option>
              </select>
            </div>
            <div className="form-group">
              <label>Training Category</label>
              <select
                value={trainingCategory}
                onChange={(e) => setTrainingCategory(e.target.value)}
                className="form-input"
              >
                <option value="all">All Categories</option>
                <option value="MTB">MTB - Minor Training Bite</option>
                <option value="DPT">DPT - Department Training</option>
              </select>
            </div>
          </div>
        </div>

        <div className="filter-actions">
          <button onClick={fetchReport} className="fetch-btn" disabled={loading}>
            {loading ? 'Loading...' : 'Generate Report'}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {reportData && (() => {
        const s = reportData.summary;
        const avgPerEmployee = s.unique_employees > 0 ? s.total_hours / s.unique_employees : 0;

        // Department rows sorted by hours (desc), excluding empty/null
        const deptRows = Object.entries(s.by_department || {})
          .filter(([name]) => name && name !== 'null')
          .map(([name, d]) => ({ name, ...d }))
          .sort((a, b) => b.hours - a.hours);
        const maxDeptHours = deptRows.length ? Math.max(...deptRows.map(d => d.hours)) : 0;

        // Role breakdown
        const trainerHours = s.by_role?.trainer?.hours || 0;
        const traineeHours = s.by_role?.trainee?.hours || 0;
        const trainerCount = s.by_role?.trainer?.count || 0;
        const traineeCount = s.by_role?.trainee?.count || 0;
        const roleTotal = trainerHours + traineeHours || 1;
        const trainerPct = (trainerHours / roleTotal) * 100;
        const traineePct = (traineeHours / roleTotal) * 100;

        return (
        <>
          {/* Stat tiles */}
          <div className="stat-tiles">
            <div className="stat-tile">
              <div className="stat-tile-label">Total Sessions</div>
              <div className="stat-tile-value">{s.total_records}</div>
              <div className="stat-tile-hint">training records</div>
            </div>
            <div className="stat-tile accent">
              <div className="stat-tile-label">Total Hours</div>
              <div className="stat-tile-value">{formatHours(s.total_hours)}</div>
              <div className="stat-tile-hint">{s.total_hours.toFixed(2)} hrs total</div>
            </div>
            <div className="stat-tile">
              <div className="stat-tile-label">Employees Trained</div>
              <div className="stat-tile-value">{s.unique_employees}</div>
              <div className="stat-tile-hint">unique people</div>
            </div>
            <div className="stat-tile">
              <div className="stat-tile-label">Avg / Employee</div>
              <div className="stat-tile-value">{formatHours(avgPerEmployee)}</div>
              <div className="stat-tile-hint">hours per person</div>
            </div>
          </div>

          <div className="charts-row">
            {/* Department bar chart */}
            <div className="chart-card">
              <h3 className="chart-title">Training Hours by Department</h3>
              {deptRows.length === 0 ? (
                <p className="chart-empty">No department data</p>
              ) : (
                <div className="bar-chart">
                  {deptRows.map((d) => (
                    <div key={d.name} className="bar-row">
                      <div className="bar-label" title={d.name}>{d.name}</div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{ width: `${maxDeptHours > 0 ? (d.hours / maxDeptHours) * 100 : 0}%` }}
                        ></div>
                        <span className="bar-value">{formatHours(d.hours)}</span>
                      </div>
                      <div className="bar-meta">{d.employees} ppl · {d.count} sess</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Role breakdown */}
            <div className="chart-card">
              <h3 className="chart-title">Trainer vs Trainee Hours</h3>
              <div className="role-split-bar">
                <div className="role-segment trainer" style={{ width: `${trainerPct}%` }}></div>
                <div className="role-segment trainee" style={{ width: `${traineePct}%` }}></div>
              </div>
              <div className="role-legend">
                <div className="role-legend-item">
                  <span className="role-dot trainer"></span>
                  <div>
                    <div className="role-legend-label">Trainer</div>
                    <div className="role-legend-value">{formatHours(trainerHours)} · {trainerCount} sessions</div>
                  </div>
                </div>
                <div className="role-legend-item">
                  <span className="role-dot trainee"></span>
                  <div>
                    <div className="role-legend-label">Trainee</div>
                    <div className="role-legend-value">{formatHours(traineeHours)} · {traineeCount} sessions</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="report-actions">
            <button onClick={exportToExcel} className="export-btn">
              📊 Export to Excel
            </button>
            <button onClick={exportToPDF} className="export-btn export-btn-pdf">
              📄 Export to PDF
            </button>
          </div>

          <div className="report-table-container">
            <table className="report-table">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>Department</th>
                  <th>Role</th>
                  <th>Date</th>
                  <th>Topic</th>
                  <th>Hours</th>
                  <th>Trainer</th>
                </tr>
              </thead>
              <tbody>
                {reportData.records.map((record, index) => (
                  <tr key={record.id}>
                    <td>{index + 1}</td>
                    <td>{record.employee_id}</td>
                    <td>{record.employee_name}</td>
                    <td>{record.department || '-'}</td>
                    <td>
                      <span className={`role-badge ${record.role}`}>
                        {record.role === 'trainer' ? 'Trainer' : 'Trainee'}
                      </span>
                    </td>
                    <td>{record.training_date}</td>
                    <td>{record.training_topic}</td>
                    <td>{record.training_hours}</td>
                    <td>{record.trainer_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
        );
      })()}
    </div>
  );
}

export default Reports;
